import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillTestGenerateResponse,
  SkillTestQuestion,
  SkillTestResultResponse,
} from '../../../models/skill-learning.model';

export type TestViewState = 'idle' | 'generating' | 'testing' | 'result' | 'error';

interface HttpStatusError {
  status: number;
}

interface TestQuestionViewModel {
  originalIndex: number;
  questionText: string;
  answers: string[];
}

@Component({
  selector: 'app-test-container',
  templateUrl: './test-container.component.html',
  styleUrls: ['./test-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestContainerComponent implements OnInit, OnDestroy {

  @Input() specializationId: string;
  @Input() isLevelPassed: boolean = false;
  @Input() testRetakeCooldownHours: number = 10;
  @Input() testGenerationLimitPerUserPerDay: number = 2;
  @Input() showCloseButton: boolean = true;
  @Input() showPassedAction: boolean = true;

  @Output() testCompleted = new EventEmitter<SkillTestResultResponse>();
  @Output() level2Requested = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  state: TestViewState = 'idle';
  test: SkillTestGenerateResponse;
  displayedQuestions: TestQuestionViewModel[] = [];
  selectedAnswers: { [questionIndex: string]: number } = {};
  result: SkillTestResultResponse;
  cooldownSeconds: number = 0;
  errorMessage: string = '';
  errorMessageParams: { [key: string]: number } = {};
  errorHelpMessage: string = '';
  errorHelpMessageParams: { [key: string]: number } = {};
  timeRemaining: number = 0;
  isSubmitting: boolean = false;

  private destroy$ = new Subject<void>();
  private timerInterval: NodeJS.Timer | null = null;
  private testDeadlineAt: number = 0;

  constructor(
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.clearTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get retryHintParams(): { [key: string]: number } {
    return { hours: this.testRetakeCooldownHours };
  }

  get retryRuleParams(): { [key: string]: number } {
    return this.buildRetryRuleParams();
  }

  get shouldShowRetryRuleHintBeforeStart(): boolean {
    return !this.isLevelPassed;
  }

  get canShowStartCard(): boolean {
    return !this.isLevelPassed;
  }

  get canShowPassedAction(): boolean {
    return this.showPassedAction && !!this.result && this.result.passed;
  }

  get canShowResultRetryAction(): boolean {
    return !!this.result;
  }

  get formattedTimeRemaining(): string {
    const minutes = Math.floor(this.timeRemaining / 60);
    const seconds = this.timeRemaining % 60;
    return `${this.formatTwoDigits(minutes)}:${this.formatTwoDigits(seconds)}`;
  }

  get shouldShowHeaderTimer(): boolean {
    return this.state === 'testing';
  }

  get canShowWorkspaceBackground(): boolean {
    return this.canShowSystemCard;
  }

  get canShowSystemCard(): boolean {
    return this.state !== 'testing';
  }

  get canShowErrorCloseAction(): boolean {
    return false;
  }

  onStartTest(): void {
    if (this.isLevelPassed) {
      return;
    }
    this.state = 'generating';
    this.clearErrorMessage();
    this.cdr.markForCheck();

    this.facade.generateTest(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        test => {
          this.test = test;
          this.displayedQuestions = this.createDisplayedQuestions(test);
          this.selectedAnswers = {};
          this.state = 'testing';
          this.startTimer(test.timeLimitMinutes);
          this.cdr.markForCheck();
        },
        error => this.handleTestError(error),
      );
  }

  onAnswerSelected(originalIndex: number, answerIndex: number): void {
    this.selectedAnswers[String(originalIndex)] = answerIndex;
    this.cdr.markForCheck();
  }

  onSubmitTest(): void {
    if (!this.canSubmitAnswers) {
      return;
    }
    this.submitCurrentTest();
  }

  onClose(): void {
    this.clearTimer();
    this.closed.emit();
    this.cdr.markForCheck();
  }

  onContinueToLevel2(): void {
    this.level2Requested.emit();
  }

  onRetryTest(): void {
    this.resetTestResult();
    this.onStartTest();
  }

  get hasSelectedAnswers(): boolean {
    return Object.keys(this.selectedAnswers).length > 0;
  }

  get canSubmitAnswers(): boolean {
    return this.hasSelectedAnswers && !this.isSubmitting;
  }

  trackByQuestionIndex(index: number, question: TestQuestionViewModel): number {
    return question ? question.originalIndex : index;
  }

  trackByAnswerIndex(index: number): number {
    return index;
  }

  isAnswerSelected(originalIndex: number, answerIndex: number): boolean {
    return this.selectedAnswers[String(originalIndex)] === answerIndex;
  }

  private startTimer(minutes: number): void {
    this.testDeadlineAt = Date.now() + minutes * 60 * 1000;
    this.timeRemaining = this.calculateTimeRemaining();
    this.cdr.markForCheck();
    this.timerInterval = setInterval(() => {
      this.timeRemaining = this.calculateTimeRemaining();
      this.cdr.markForCheck();
      this.tryAutoSubmitExpiredTest();
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.cdr.markForCheck();
    }
    this.testDeadlineAt = 0;
  }

  private autoSubmitTest(): void {
    if (!this.canAutoSubmitTest()) {
      return;
    }
    this.submitCurrentTest();
  }

  private handleTestError(error: HttpStatusError): void {
    this.state = 'error';
    this.clearErrorMessage();

    if (error.status === 409) {
      this.errorMessage = 'SKILL_LEARNING_TEST_ERROR_GENERIC';
      this.errorMessageParams = {};
    } else if (error.status === 429) {
      this.loadCooldownWithFallback();
    } else if (error.status === 503) {
      this.errorMessage = 'SKILL_LEARNING_TEST_ERROR_SERVICE_UNAVAILABLE';
      this.errorMessageParams = {};
    } else {
      this.errorMessage = 'SKILL_LEARNING_TEST_ERROR_GENERIC';
      this.errorMessageParams = {};
    }
    this.cdr.markForCheck();
  }

  private loadCooldownWithFallback(): void {
    this.facade.getTestCooldown(this.specializationId)
      .takeUntil(this.destroy$)
      .subscribe(
        seconds => this.handleCooldownLoaded(seconds),
        () => this.showTestGenerationLimitError(),
      );
  }

  private handleCooldownLoaded(seconds: number): void {
    if (seconds > 0) {
      this.cooldownSeconds = seconds;
      this.formatCooldown();
      this.cdr.markForCheck();
      return;
    }
    this.showTestGenerationLimitError();
  }

  private formatCooldown(): void {
    const totalMinutes = Math.max(1, Math.ceil(this.cooldownSeconds / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
      this.errorMessage = 'SKILL_LEARNING_TEST_RETRY_IN_HOURS';
      this.errorMessageParams = { hours: hours, minutes: minutes };
      this.setRetryRuleHelpMessage();
      return;
    }
    this.errorMessage = 'SKILL_LEARNING_TEST_RETRY_IN_MINUTES';
    this.errorMessageParams = { minutes: minutes };
    this.setRetryRuleHelpMessage();
  }

  private resetTestResult(): void {
    this.result = null;
    this.selectedAnswers = {};
    this.displayedQuestions = [];
    this.test = null;
    this.state = 'idle';
    this.isSubmitting = false;
    this.clearErrorMessage();
  }

  private clearErrorMessage(): void {
    this.errorMessage = '';
    this.errorMessageParams = {};
    this.errorHelpMessage = '';
    this.errorHelpMessageParams = {};
  }

  private showTestGenerationLimitError(): void {
    this.errorMessage = 'SKILL_LEARNING_TEST_DAILY_LIMIT_REACHED';
    this.errorMessageParams = {
      limit: this.testGenerationLimitPerUserPerDay,
    };
    this.setRetryRuleHelpMessage();
    this.cdr.markForCheck();
  }

  private setRetryRuleHelpMessage(): void {
    this.errorHelpMessage = 'SKILL_LEARNING_TEST_RETRY_RULE_HINT';
    this.errorHelpMessageParams = this.buildRetryRuleParams();
  }

  private buildRetryRuleParams(): { [key: string]: number } {
    return {
      hours: this.testRetakeCooldownHours,
      limit: this.testGenerationLimitPerUserPerDay,
    };
  }

  private submitCurrentTest(): void {
    this.isSubmitting = true;
    this.clearTimer();
    this.state = 'generating';
    this.cdr.markForCheck();
    this.facade.submitTest(this.specializationId, this.selectedAnswers)
      .takeUntil(this.destroy$)
      .subscribe(
        result => this.handleSubmitSuccess(result),
        error => this.handleSubmitError(error),
      );
  }

  private handleSubmitSuccess(result: SkillTestResultResponse): void {
    this.isSubmitting = false;
    this.result = result;
    this.state = 'result';
    this.testCompleted.emit(result);
    this.cdr.markForCheck();
  }

  private handleSubmitError(error: HttpStatusError): void {
    this.isSubmitting = false;
    this.handleTestError(error);
  }

  private calculateTimeRemaining(): number {
    if (!this.testDeadlineAt) {
      return 0;
    }
    return Math.max(0, Math.ceil((this.testDeadlineAt - Date.now()) / 1000));
  }

  private tryAutoSubmitExpiredTest(): void {
    if (this.state !== 'testing' || this.timeRemaining > 0) {
      return;
    }
    this.autoSubmitTest();
  }

  private canAutoSubmitTest(): boolean {
    return !!this.test && !this.isSubmitting;
  }

  private formatTwoDigits(value: number): string {
    return value < 10 ? `0${value}` : String(value);
  }

  private createDisplayedQuestions(test: SkillTestGenerateResponse): TestQuestionViewModel[] {
    if (!test || !test.questions) {
      return [];
    }
    return this.shuffleQuestions(this.mapQuestions(test.questions));
  }

  private mapQuestions(questions: SkillTestQuestion[]): TestQuestionViewModel[] {
    return questions.map((question, index) => ({
      originalIndex: index,
      questionText: question.questionText,
      answers: question.answers,
    }));
  }

  private shuffleQuestions(questions: TestQuestionViewModel[]): TestQuestionViewModel[] {
    const shuffled = questions.slice();
    let currentIndex = shuffled.length - 1;
    while (currentIndex > 0) {
      this.swapQuestions(shuffled, currentIndex, this.randomIndex(currentIndex + 1));
      currentIndex -= 1;
    }
    return shuffled;
  }

  private swapQuestions(questions: TestQuestionViewModel[], leftIndex: number, rightIndex: number): void {
    const current = questions[leftIndex];
    questions[leftIndex] = questions[rightIndex];
    questions[rightIndex] = current;
  }

  private randomIndex(limit: number): number {
    return Math.floor(Math.random() * limit);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/test/test-container.component.ts